import { Fragment, useState } from 'react';
import { ChevronRight, Clock } from 'lucide-react';
import { getPatient } from 'library/auth';
import { getInitials } from 'utils/helpers';
import { getProfileCompletion } from 'pages/Profile/helper';
import ProfileModal from './ProfileModal';
import { CARD, SECTION, getProfileCards, getProfileSections } from './helper';

const SectionIcon = ({ icon: Icon, iconBg, iconColor, carrier = null }) => {
  if (carrier?.logo_url)
    return (
      <span className="w-8 h-8 rounded-xl overflow-hidden bg-white shadow-sm shrink-0">
        <img
          src={carrier.logo_url}
          alt={carrier.title || 'Insurance carrier'}
          className="h-full w-full object-cover"
        />
      </span>
    );

  if (carrier?.title)
    return (
      <span className="w-8 h-8 rounded-xl bg-white shadow-sm flex items-center justify-center shrink-0 text-[11px] font-extrabold text-teamcare-blue-dark">
        {getInitials(carrier.title)}
      </span>
    );

  return (
    <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>
      <Icon size={16} className={iconColor} />
    </div>
  );
};

const Profile = ({ profile, onRefresh }) => {
  const [stepId, setStepId] = useState(null);
  const patient = getPatient();
  const sections = getProfileSections(profile);
  const completion = getProfileCompletion(profile?.attributes);

  const closeModal = () => {
    setStepId(null);
    onRefresh();
  };

  return (
    <div className={`${CARD} p-4`}>
      <div className="flex items-center gap-3 pb-3.5 border-b border-gray-100">
        <div className="w-11 h-11 rounded-full bg-teamcare-blue text-white flex items-center justify-center text-sm font-bold shrink-0">
          {getInitials(patient.full_name)}
        </div>

        <div className="min-w-0">
          <p className="text-sm font-bold text-secondary-900">My Patient Profile</p>
          <p className="text-[11px] text-secondary-400 mt-0.5">What the practice has on file</p>
        </div>
      </div>

      <div className="space-y-2.5 mt-3">
        {getProfileCards(sections, completion).map(({ id, title, rows, empty, completed, ...icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setStepId(id)}
            aria-label={`View ${title.toLowerCase()} details`}
            className={`group w-full text-left cursor-pointer transition-colors hover:bg-secondary-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${SECTION}`}
          >
            <div className="flex items-center gap-2">
              <SectionIcon {...icon} />
              <p className="flex-1 min-w-0 line-clamp-2 break-words text-[13px] font-semibold leading-tight text-secondary-900">
                {title}
              </p>

              {!completed && (
                <span className="inline-flex shrink-0 items-center gap-0.5 rounded-md bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-amber-700">
                  <Clock size={10} />
                  Pending
                </span>
              )}
            </div>

            {empty ? (
              <p className="text-[11px] text-secondary-400 mt-3">{empty}</p>
            ) : (
              <div className="grid grid-cols-[6rem_minmax(0,1fr)] lg:grid-cols-[auto_minmax(0,1fr)] xl:grid-cols-[6rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 mt-3">
                {rows.map(({ label, value }) => (
                  <Fragment key={label}>
                    <p className="whitespace-nowrap text-[11px] text-secondary-400">{label}</p>
                    <p title={value} className="line-clamp-2 text-[11px] font-semibold text-secondary-900 [overflow-wrap:anywhere]">
                      {value}
                    </p>
                  </Fragment>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-3">
              <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-secondary-900 group-hover:text-teamcare-blue transition-colors">
                View details
                <ChevronRight size={13} />
              </span>
            </div>
          </button>
        ))}
      </div>

      <ProfileModal
        stepId={stepId}
        data={sections[stepId]}
        practice={sections.practice}
        completed={completion[stepId]}
        onClose={closeModal}
      />
    </div>
  );
};

export default Profile;
